import React, { useEffect, useMemo, useState } from 'react';
import { getApiUrl } from '../../helpers/url_helper';
import { useThemeMode } from '../../ThemeContext';

const FolderOutlineIcon = ({ color = 'currentColor', size = 26 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M3 7a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
  </svg>
);

function formatItemDate(raw) {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const KebabIcon = ({ color = 'currentColor', size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="12" cy="5" r="1.4" />
    <circle cx="12" cy="12" r="1.4" />
    <circle cx="12" cy="19" r="1.4" />
  </svg>
);

export default function AssistantProjectStartView({
  mode: modeProp = 'light',
  projectId = null,
  projectTitle = 'Project',
  items = [],
  onSubmit,
  onSessionClick
}) {
  const { mode: contextMode } = useThemeMode();
  const mode = contextMode || modeProp;
  const [inputValue, setInputValue] = useState('');
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [historyError, setHistoryError] = useState('');
  const [openMenuSessionKey, setOpenMenuSessionKey] = useState(null);
  const [hoveredSessionKey, setHoveredSessionKey] = useState(null);
  const [editingSessionKey, setEditingSessionKey] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);

  useEffect(() => {
    const pid = projectId != null ? String(projectId) : '';
    if (!pid) {
      setSessions([]);
      setIsLoading(false);
      setLoadError('');
      return;
    }

    const controller = new AbortController();

    const fetchSessions = async () => {
      setIsLoading(true);
      setLoadError('');
      try {
        const token = localStorage.getItem('authToken');
        const endpoint = `company/conversation-projects/${encodeURIComponent(pid)}/chat-sessions`;

        const res = await fetch(getApiUrl(endpoint), {
          method: 'GET',
          headers: {
            accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          signal: controller.signal
        });

        const json = await res.json().catch(() => null);
        if (!res.ok) {
          const msg = json?.message || `Request failed (${res.status})`;
          throw new Error(msg);
        }

        const chatSessions = json?.data?.chat_sessions;
        setSessions(Array.isArray(chatSessions) ? chatSessions : []);
      } catch (e) {
        if (e?.name === 'AbortError') return;
        setLoadError(e?.message || 'Failed to load sessions');
        setSessions([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSessions();

    return () => controller.abort();
  }, [projectId]);

  const palette = useMemo(() => {
    const isDark = mode === 'dark';
    return {
      pageBg: 'transparent',
      text: isDark ? '#ffffff' : '#1a1a1a',
      muted: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)',
      border: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
      rowBorder: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)',
      inputBg: isDark ? '#141414' : '#ffffff'
    };
  }, [mode]);

  const handleSubmit = () => {
    const text = inputValue.trim();
    if (!text) return;
    onSubmit?.(text);
    setInputValue('');
  };

  const getSessionKey = (item) => {
    const key = item?.chat_session_id ?? item?.sessionId ?? item?.id;
    return key != null ? String(key) : '';
  };

  const getSessionTitle = (item) => item?.title || 'Conversation';

  const saveEditedTitle = async (item) => {
    const sessionId = item?.chat_session_id ?? item?.sessionId ?? item?.id;
    const key = getSessionKey(item);
    const newTitle = (editingTitle || '').trim();
    const oldTitle = getSessionTitle(item).trim();

    if (!key || sessionId == null || isSavingTitle) return;
    if (!newTitle) {
      setEditingTitle(oldTitle);
      setEditingSessionKey(null);
      return;
    }
    if (newTitle === oldTitle) {
      setEditingSessionKey(null);
      return;
    }

    setIsSavingTitle(true);
    setHistoryError('');
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(getApiUrl(`company/chat-sessions/${sessionId}/title`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ title: newTitle })
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.status !== 'success') {
        throw new Error(data?.message || 'Failed to update chat title. Please try again.');
      }

      const resolvedTitle = data?.data?.title || newTitle;
      setSessions((prev) =>
        prev.map((s) =>
          String(getSessionKey(s)) === key ? { ...s, title: resolvedTitle } : s
        )
      );
    } catch (e) {
      setHistoryError(e?.message || 'Failed to update chat title. Please try again.');
    } finally {
      setIsSavingTitle(false);
      setEditingSessionKey(null);
    }
  };

  const deleteSession = async (item) => {
    const sessionId = item?.chat_session_id ?? item?.sessionId ?? item?.id;
    const key = getSessionKey(item);
    if (!key || sessionId == null) return;

    setOpenMenuSessionKey(null);
    setHistoryError('');
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(getApiUrl(`company/chat-sessions/${sessionId}`), {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.status !== 'success') {
        throw new Error(data?.message || 'Failed to delete chat. Please try again.');
      }

      setSessions((prev) => prev.filter((s) => String(getSessionKey(s)) !== key));
    } catch (e) {
      setHistoryError(e?.message || 'Failed to delete chat. Please try again.');
    }
  };

  return (
    <div
      className="assistant-main-column"
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        background: palette.pageBg,
        color: palette.text
      }}
    >
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '32px 24px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <FolderOutlineIcon color={palette.text} size={24} />
          <div style={{ fontSize: 24, fontWeight: 600, lineHeight: 1.15 }}>{projectTitle}</div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            background: mode === 'dark'
              ? 'linear-gradient(135deg, #18191B 0%, #232323 100%)'
              : 'linear-gradient(135deg, #f8f9fa 0%, #e0f7f7 100%)',
            border: `1px solid ${mode === 'dark' ? '#232323' : '#ddd'}`,
            borderRadius: 14,
            minHeight: 56,
            boxShadow: mode === 'dark' ? '0 1px 4px rgba(0,0,0,0.12)' : '0 1px 4px rgba(38,195,192,0.04)',
            padding: '0 16px',
          }}
        >
          <span style={{ fontSize: 22, color: palette.muted, marginRight: 10, lineHeight: 1 }}>+</span>
          <input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder={`New chat in ${projectTitle}`}
            style={{
              flex: 1,
              height: 56,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: palette.text,
              fontSize: 16,
              fontWeight: 500,
              fontFamily: 'inherit',
              padding: '18px 0',
            }}
          />
        </div>

        <div
          style={{
            marginTop: 18,
            maxWidth: 640,
            marginLeft: 'auto',
            marginRight: 'auto',
            overflow: 'visible'
          }}
        >
          {!!historyError && (
            <div style={{ color: '#f44336', fontSize: 12, padding: '4px 0 8px' }}>
              {historyError}
            </div>
          )}
          {isLoading && (
            <div style={{ color: palette.muted, fontSize: 13, padding: '8px 0' }}>
              Loading conversations...
            </div>
          )}
          {!!loadError && !isLoading && (
            <div style={{ color: palette.muted, fontSize: 13, padding: '8px 0' }}>
              {loadError}
            </div>
          )}

          {sessions
            .slice(0, 6)
            .map((item, idx) => (
            <div
              key={String(item?.chat_session_id ?? item?.id ?? item?.sessionId ?? idx)}
              onClick={() =>
                onSessionClick?.({
                  id: item?.chat_session_id ?? item?.id ?? item?.sessionId,
                  sessionId: item?.chat_session_id ?? item?.sessionId ?? item?.id,
                  title: item?.title || 'Conversation',
                  createdAt: item?.createdAt || item?.created_at
                })
              }
              style={{
                borderBottom: `1px solid ${palette.rowBorder}`,
                padding: '14px 0 12px',
                cursor: 'pointer',
                overflow: 'visible'
              }}
              onMouseEnter={() => setHoveredSessionKey(getSessionKey(item))}
              onMouseLeave={() =>
                setHoveredSessionKey((prev) =>
                  prev === getSessionKey(item) ? null : prev
                )
              }
            >
              <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  {editingSessionKey === getSessionKey(item) ? (
                    <input
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onBlur={() => saveEditedTitle(item)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          saveEditedTitle(item);
                        }
                        if (e.key === 'Escape') {
                          setEditingSessionKey(null);
                          setEditingTitle(getSessionTitle(item));
                        }
                      }}
                      style={{
                        width: '100%',
                        height: 28,
                        borderRadius: 6,
                        border: `1px solid ${mode === 'dark' ? '#444' : '#ccc'}`,
                        background: mode === 'dark' ? '#111' : '#fff',
                        color: palette.text,
                        padding: '0 8px',
                        fontSize: 13,
                        fontFamily: 'inherit'
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        fontSize: 16,
                        lineHeight: 1.15,
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {getSessionTitle(item)}
                    </div>
                  )}
                  <div
                    style={{
                      marginTop: 4,
                      color: palette.muted,
                      fontSize: 13,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {formatItemDate(item?.createdAt || item?.created_at)}
                  </div>
                </div>

                {editingSessionKey !== getSessionKey(item) && (
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const key = getSessionKey(item);
                        setOpenMenuSessionKey((prev) => (prev === key ? null : key));
                      }}
                      style={{
                        width: 24,
                        height: 24,
                        border: 'none',
                        borderRadius: 6,
                        display:
                          hoveredSessionKey === getSessionKey(item) ||
                          openMenuSessionKey === getSessionKey(item)
                            ? 'flex'
                            : 'none',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                        color: palette.text,
                        cursor: 'pointer'
                      }}
                    >
                      <KebabIcon color={palette.text} size={14} />
                    </button>

                    {openMenuSessionKey === getSessionKey(item) && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: 'absolute',
                          bottom: 26,
                          right: 0,
                          minWidth: 120,
                          borderRadius: 8,
                          border: mode === 'dark' ? '1px solid #333' : '1px solid #e6e6e6',
                          background: mode === 'dark' ? '#1a1a1a' : '#fff',
                          boxShadow: '0 10px 24px rgba(0,0,0,0.18)',
                          zIndex: 9999,
                          padding: 6
                        }}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuSessionKey(null);
                            setEditingSessionKey(getSessionKey(item));
                            setEditingTitle(getSessionTitle(item));
                          }}
                          style={{
                            width: '100%',
                            border: 'none',
                            background: 'transparent',
                            textAlign: 'left',
                            padding: '8px 10px',
                            borderRadius: 6,
                            color: palette.text,
                            cursor: 'pointer',
                            fontSize: 13
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteSession(item);
                          }}
                          style={{
                            width: '100%',
                            border: 'none',
                            background: 'transparent',
                            textAlign: 'left',
                            padding: '8px 10px',
                            borderRadius: 6,
                            color: '#f44336',
                            cursor: 'pointer',
                            fontSize: 13
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

